import { useEffect } from 'react'
import { restoreSessionExpiredToast } from '@/lib/session-expired-toast'

/**
 * Re-raises the session-expired toast that a page reload interrupted
 * (DCP-365 AC.4.2).
 *
 * Renders nothing. It exists only to own the timing, and the timing is the
 * whole point: **this must be rendered AFTER `<Toaster />`**, as a later
 * sibling. Anywhere earlier and the toast is silently discarded.
 *
 * WHY. sonner's `ToastState.addToast` publishes to whoever is subscribed AT
 * THAT MOMENT and never replays afterwards:
 *
 * ```js
 * this.addToast = (data) => {
 *   this.publish(data)                    // current subscribers only
 *   this.toasts = [...this.toasts, data]  // stored, but nothing re-reads it
 * }
 * ```
 *
 * `Toaster` starts from `useState([])` — not from `ToastState.toasts` — and
 * subscribes in a `useEffect`. So a toast raised before its effect has run
 * lands in sonner's state with zero subscribers and never reaches the screen.
 *
 * The first version of this feature called `restoreSessionExpiredToast()` at
 * module scope in `main.tsx`, before `renderApp()` had mounted anything, so
 * AC.4.2 never worked — the toast was created and thrown away. Caught in
 * review on PR #210.
 *
 * SIBLING ORDER, NOT JUST "IN A COMPONENT". Measured rather than reasoned
 * about, because React's effect ordering is easy to get wrong here: effects
 * run in tree order, so a sibling BEFORE `<Toaster />` still fires before the
 * Toaster subscribes. Both arrangements were tested — before: not rendered;
 * after: rendered. That also rules out hanging this off a hook inside
 * `<App />`, which sits above the Toaster in the tree.
 *
 * Safe under StrictMode's double-invoked effects: the toast carries a fixed
 * id, so a second call updates the same toast instead of stacking one.
 */
export function SessionExpiredToastRestorer() {
  useEffect(() => {
    restoreSessionExpiredToast()
  }, [])

  return null
}
